import { baseApi } from './baseApi';

export interface CodesharePayload {
  masterflightRef: string;
  seasonId: string;
  flightOwner: string;
  flightNo: string;
  csStartDate: string | Date;
  csEndDate: string | Date;
  frequency: string;
  freqRate?: string; // SSIM-compliant: "" (weekly), "2" (bi-weekly), "3"-"99" (multi-week)
  codeshareFlightNo: string[];
  sector: string;
}

export interface CodeshareRecord extends CodesharePayload {
  _id: string;
  createdAt?: string;
  updatedAt?: string;
}

// All codeshare mutations also invalidate MasterFlight LIST because codeshares
// are embedded into flight records by the with-codeshares endpoint.
export const codeshareApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({

    getCodesharesByMasterFlight: builder.query<CodeshareRecord[], string>({
      query: (masterFlightId) => `/codeshare/master/${masterFlightId}`,
      providesTags: (result, error, masterFlightId) =>
        result
          ? [
              ...result.map(({ _id }) => ({ type: 'Codeshare' as const, id: _id })),
              { type: 'Codeshare', id: `MASTER_${masterFlightId}` },
            ]
          : [{ type: 'Codeshare', id: `MASTER_${masterFlightId}` }],
    }),

    createCodeshare: builder.mutation<CodeshareRecord, CodesharePayload>({
      query: (payload) => ({
        url: '/codeshare',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (result, error, payload) => [
        { type: 'Codeshare', id: `MASTER_${payload.masterflightRef}` },
        { type: 'MasterFlight', id: 'LIST' },
        { type: 'MasterFlight', id: payload.masterflightRef },
      ],
    }),

    updateCodeshare: builder.mutation<CodeshareRecord, { id: string; payload: Partial<CodesharePayload> }>({
      query: ({ id, payload }) => ({
        url: `/codeshare/${id}`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: (result, error, { id, payload }) => [
        { type: 'Codeshare', id },
        { type: 'MasterFlight', id: 'LIST' },
        ...(payload.masterflightRef ? [{ type: 'MasterFlight' as const, id: payload.masterflightRef }] : []),
      ],
    }),

    deleteCodeshare: builder.mutation<void, { id: string; masterflightRef: string }>({
      query: ({ id }) => ({
        url: `/codeshare/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { id, masterflightRef }) => [
        { type: 'Codeshare', id },
        { type: 'Codeshare', id: `MASTER_${masterflightRef}` },
        { type: 'MasterFlight', id: 'LIST' },
        { type: 'MasterFlight', id: masterflightRef },
      ],
    }),
  }),
});

export const {
  useGetCodesharesByMasterFlightQuery,
  useLazyGetCodesharesByMasterFlightQuery,
  useCreateCodeshareMutation,
  useUpdateCodeshareMutation,
  useDeleteCodeshareMutation,
} = codeshareApi;
